import React, { forwardRef, useEffect, useState, useRef, useCallback, useMemo } from "react";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { BASE_URL } from "../../../api/axios";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { createTheme } from "@mui/material";
import "react-lazy-load-image-component/src/effects/blur.css";
import ViewFramesModal from "./ViewFramesModal";
import "./ViewEventsTable.css";
import useAuth from "../../../hooks/useAuth";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { EVENT_COLORS } from "../../../constants.js";
import { useOutletContext } from "react-router-dom";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBoxIcon {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => (
    <DeleteOutlineIcon {...props} ref={ref} />
  )),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRightIcon {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAltIcon {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterAltIcon {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPageIcon {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => (
    <ChevronRightIcon {...props} ref={ref} />
  )),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeftIcon {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => (
    <ArrowDownwardIcon {...props} ref={ref} />
  )),
  ThirdStateCheck: forwardRef((props, ref) => (
    <RemoveIcon {...props} ref={ref} />
  )),
  ViewColumn: forwardRef((props, ref) => (
    <ViewColumnIcon {...props} ref={ref} />
  )),
};

const ViewEventsTable = ({ preloadData, deviceFilter, searchTerm, selectedDate, statusFilter, operatorStatusFilter }) => {
  const axiosPrivate = useAxiosPrivate();
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});
  const [filter, setFilter] = useState(false);
  const tableRef = useRef();
  const [showImage, setShowImage] = useState(false);
  const [pageNumber] = useState(null);
  const [orderBy] = useState(null);
  const [muiTableKey, setMuiTableKey] = useState(0);
  const { setAuth } = useAuth();
  const [filterTableData, setFilterTableData] = useState({});
  const [filterActive, setFilterActive] = useState(false);
  const [pageSize] = useState(10);
  const [eventData, setEventData] = useState([]);
  const [modalSide, setModalSide] = useState("side1");
  // const [selectedDate, setSelectedDate] = useState(null);
  const preloadRef = useRef(preloadData);

  const styles = {
    pill: {
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      width: "fit-content",
      margin: "auto",
    },
    success_pill: {
      color: EVENT_COLORS.SUCCESS_COLOR,
      backgroundColor: EVENT_COLORS.SUCCESS_SECONDARY_COLOR,
    },
    defective_pill: {
      color: EVENT_COLORS.DEFECT_COLOR,
      backgroundColor: EVENT_COLORS.DEFECT_SECONDARY_COLOR,
    },
    defective_warn_pill: {
      color: EVENT_COLORS.WARN_COLOR,
      backgroundColor: EVENT_COLORS.WARN_SECONDARY_COLOR,
    },
    processing_pill: {
      color: "#e28759ff",
      backgroundColor: "#fef0e0ff",
    },
    failed_pill: {
      color: "#000",
      backgroundColor: "#e9e9e9ff",
    },
  };


  const controller = useMemo(() => new AbortController(), []);
  const getEvents = useCallback(
    async (url) => {
      try {
        const userResponse = await axiosPrivate.get(url, {
          signal: controller.signal,
        });
        return userResponse.data;
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.log(err);
        }
      }
    },
    [axiosPrivate, controller.signal],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tableRef.current) {
        preloadRef.current = null;
        tableRef.current.onQueryChange();
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedDate, deviceFilter, statusFilter, operatorStatusFilter]);

  let url;

  const handleOpenModal = (data, side = "side1") => {
    setOpenModal(true);
    setSelectedImage(data);
    setEventData(data);
    setModalSide(side);
  };

  useEffect(() => {
    setMuiTableKey((m) => m + 1);
  }, [filterActive, filterTableData?.events]);

  useEffect(() => {
    if (!filter) {
      setFilterTableData([]);
      setFilterActive(false);
    }
  }, [filter]);

  const handeleTableReload = () => {
    tableRef.current && tableRef.current.onQueryChange();
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [deviceFilter, statusFilter, operatorStatusFilter]);

  useEffect(() => {
    const interval = setInterval(() => {
      let reloadUrl = `/defect-inspection/v1/events?page=${pageNumber}&limit=${pageSize}`;
      getEvents(reloadUrl).then((data) => {
        if (data.events.length > 0) {
          let latest_event = data.events[0];
          if (tableRef.current?.state.data.length > 0) {
            let event = JSON.parse(
              JSON.stringify(tableRef.current.state.data[0]),
            );
            delete event.tableData;

            if (JSON.stringify(event) !== JSON.stringify(latest_event)) {
              console.log("Reloading..");
              handeleTableReload();
            }
          }
        }
      });
    }, 120000);
    return () => clearInterval(interval);
  }, [pageNumber, pageSize, getEvents]);

  useEffect(() => {
    setShowImage(false);
  }, [pageNumber, orderBy]);

  const handleCloseModal = () => {
    setOpenModal(false);
  };


  const makeDateReadable = (stringValue) => {
    const newDate = new Date(stringValue + " UTC");
    const finalResult = newDate.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return finalResult;
  };


  const isDefectiveOrWarn = (event) => {
    let side1_measurements = event?.side1_prediction?.measurements;
    let side2_measurements = event?.side2_prediction?.measurements;

    let isSide1DefectiveColor = side1_measurements?.some((element) => {
      return (
        Math.abs(element.predicted_length - element.length) >
        event?.side1_prediction?.threshold
      );
    });
    let isSide2DefectiveColor = side2_measurements?.some((element) => {
      return (
        Math.abs(element.predicted_length - element.length) >
        event?.side2_prediction?.threshold
      );
    });

    return isSide1DefectiveColor || isSide2DefectiveColor;
  };

  const columns = [
    {
      title: "Edge ID",
      field: "edge_event_id",
      sorting: false,
      filtering: false,
      render: (rowData) => <div>{rowData?.edge_event_id}</div>,
    },
    {
      title: "Device Name",
      field: "device_name",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <div style={{ textTransform: "capitalize" }}>
          {rowData?.device_display_name || rowData?.device_name || "-"}
        </div>
      ),
    },
    {
      title: "PID",
      field: "workpiece_id",
      sorting: false,
      filtering: false,
    },
    {
      title: "Order Number",
      field: "order_number",
      sorting: false,
      filtering: false,
    },
    {
      title: "Profile",
      field: "profile_info.profile",
      sorting: false,
      filtering: false,
    },
    {
      title: "Status",
      field: "status",
      sorting: false,
      filtering: false,
      render: (rowData) => {
        const origStatus =
          rowData.status === "defective"
            ? "REJECT"
            : rowData.status === "good workpiece"
              ? "PASS"
              : rowData.status;

        const operatorFeedback = rowData?.operator_feedback || rowData?.additional_info?.operator_feedback;
        const overallOverride =
          operatorFeedback?.overall?.feedback_status ||
          operatorFeedback?.side1?.feedback_status ||
          operatorFeedback?.side2?.feedback_status;

        const hasOverride = overallOverride && overallOverride !== origStatus;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
            <div
              style={
                rowData.status === "processing"
                  ? { ...styles.pill, ...styles.processing_pill }
                  : rowData.status === "failed"
                    ? { ...styles.pill, ...styles.failed_pill }
                    : rowData?.status === "defective" && isDefectiveOrWarn(rowData)
                      ? { ...styles.pill, ...styles.defective_pill }
                      : rowData?.status === "defective" &&
                          !isDefectiveOrWarn(rowData)
                        ? { ...styles.pill, ...styles.defective_warn_pill }
                        : { ...styles.pill, ...styles.success_pill }
              }
            >
              {origStatus}
            </div>
            {hasOverride && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: "Work Sans",
                  color:
                    overallOverride === "PASS"
                      ? EVENT_COLORS.SUCCESS_COLOR
                      : EVENT_COLORS.DEFECT_COLOR,
                  backgroundColor:
                    overallOverride === "PASS"
                      ? EVENT_COLORS.SUCCESS_SECONDARY_COLOR
                      : EVENT_COLORS.DEFECT_SECONDARY_COLOR,
                  padding: "2px 8px",
                  borderRadius: "10px",
                  border: `1px solid ${
                    overallOverride === "PASS"
                      ? EVENT_COLORS.SUCCESS_COLOR
                      : EVENT_COLORS.DEFECT_COLOR
                  }`,
                  display: "inline-block",
                  userSelect: "none",
                }}
              >
                Operator: {overallOverride}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Captured Image",
      field: "event.image",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <>
          <div
            variant="contained"
            type="button"
            style={{
              // background: "rgb(55 48 224)",
              width: "100%",
              display: "flex",
              gap: "0.5rem",
              cursor: "pointer",
              justifyContent: "center",
            }}
            // onClick={() => {
            //   handleOpenModal(rowData);
            // }}
          >
            <div
              onClick={() => {
                handleOpenModal(rowData, "side1");
              }}
            >
              <div className="image-container">
                <img
                  alt=""
                  onLoad={() => setShowImage(true)}
                  key={rowData.index}
                  style={{
                    height: "100%",
                    width: "100%",
                  }}
                  src={`${BASE_URL}/defect-inspection/v1/${rowData?.side1_annotated_image}?mode=thumbnail`}
                ></img>
                {!showImage && <div className="image-placeholder"></div>}
              </div>
            </div>
            <div
              onClick={() => {
                handleOpenModal(rowData, "side2");
              }}
            >
              <div className="image-container">
                <img
                  alt=""
                  onLoad={() => setShowImage(true)}
                  key={rowData.index}
                  style={{
                    height: "100%",
                    width: "100%",
                  }}
                  src={`${BASE_URL}/defect-inspection/v1/${rowData?.side2_annotated_image}?mode=thumbnail`}
                ></img>
                {!showImage && <div className="image-placeholder"></div>}
              </div>
            </div>

            {/* 
            <div className="image-container">
              <img
                onLoad={() => setShowImage(true)}
                key={rowData.index}
                style={{
                  height: "100%",
                  width: "100%",
                }}
                src={rowData.corner2_image1_thumbnail}
              ></img>
              {!showImage && <div className="image-placeholder"></div>}
            </div>
            <div className="image-container">
              <img
                onLoad={() => setShowImage(true)}
                key={rowData.index}
                style={{
                  height: "100%",
                  width: "100%",
                }}
                src={rowData.corner2_image2_thumbnail}
              ></img>
              {!showImage && <div className="image-placeholder"></div>}
            </div> */}
          </div>
          {openModal && (
            <ViewFramesModal
              selectedImage={selectedImage}
              openModal={openModal}
              handleClose={handleCloseModal}
              eventData={eventData}
              side={modalSide}
            />
          )}
        </>
      ),
    },
    {
      title: "Created Date",
      field: "created_date",
      filtering: false,
      // type: "date",
      // dateSetting: { locale: "en-GB" },
      // filterComponent: (props) => (
      //   <div style={{ display: "flex", alignItems: "center" }}>
      //     {console.log("propsss", props)}
      //     <CustomDatePicker {...props} />{" "}
      //     <div
      //       style={{ marginLeft: "10px", cursor: "pointer", color: "red" }}
      //       onClick={handleClearFilter}
      //       tooltip="Close Filter"
      //     >
      //       <ClearIcon />
      //     </div>
      //   </div>
      // ),
      render: (rowData) => <div>{makeDateReadable(rowData.created_date)}</div>,
    },
    // {
    //   title: "Actions",
    //   field: "",
    //   render: (rowData) => (
    //     <>
    //       <div style={{ position: "relative" }}>
    //         <LocalOfferIcon
    //           onClick={(e) => {
    //             handleTagin(e, rowData);
    //           }}
    //           style={{
    //             cursor: "pointer",
    //             color:
    //               tagActive && rowData.event_id === tagID
    //                 ? "#656363"
    //                 : rowData.tags.length
    //                 ? "#70ca70"
    //                 : "#6462627a",
    //           }}
    //         ></LocalOfferIcon>
    //         {tagActive && rowData.event_id === tagID && (
    //           <ClickAwayListener onClickAway={() => handleCloseActiveTag()}>
    //             <div
    //               style={{
    //                 position: "absolute",
    //                 width: "200px",
    //                 minHeight: "120px",
    //                 zIndex: 1,
    //                 borderRadius: "6px",
    //                 right: "40px",
    //                 background: "white",
    //                 bottom: bottomDrop ? "40px" : "",
    //                 padding: "12px",
    //                 boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    //               }}
    //             >
    //               <Chip
    //                 icon={RemoveIcon}
    //                 style={{ margin: "4px" }}
    //                 label="Wrong Prediction"
    //                 color={isTagged ? "primary" : "default"}
    //                 onClick={() => handleCheck(rowData)}
    //               />
    //               {/* {otherPrediction.length != 0 && (
    //                 <>
    //                   {otherPrediction.map((item, index) => {
    //                     return (
    //                       <Chip
    //                         onDelete={() => alert("hi")}
    //                         style={{ margin: "4px" }}
    //                         key={index}
    //                         label={item}
    //                         color="primary"
    //                       ></Chip>
    //                     );
    //                   })}
    //                 </>
    //               )} */}
    //               {customTags.length != 0 && (
    //                 <>
    //                   {customTags.map((item, index) => {
    //                     if (item !== "wrongprediction")
    //                       return (
    //                         <Chip
    //                           onDelete={() => handleChipDelete(item, index)}
    //                           style={{ margin: "4px" }}
    //                           key={index}
    //                           label={item}
    //                           color="primary"
    //                         ></Chip>
    //                       );

    //                     return null;
    //                   })}
    //                 </>
    //               )}
    //               <div
    //                 style={{
    //                   display: "flex",
    //                   alignItems: "center",
    //                   marginTop: "6px",
    //                 }}
    //               >
    //                 <form
    //                   onSubmit={(e) => handleFormSubmit(e)}
    //                   style={{ width: "90%" }}
    //                 >
    //                   <input
    //                     placeholder="Add Custom Tag"
    //                     style={{ height: "34px", margin: "4px", width: "96%" }}
    //                     onChange={(e) => setCustomPrediction(e.target.value)}
    //                     value={customPrediction}
    //                   ></input>
    //                 </form>
    //                 <FileUploadIcon
    //                   onClick={() => tagSubmit(rowData)}
    //                   style={{
    //                     height: "32px",
    //                     width: "32px",
    //                     marginLeft: "4px",
    //                     color: "#70ca70",
    //                     cursor: "pointer",
    //                   }}
    //                 ></FileUploadIcon>
    //               </div>
    //             </div>
    //           </ClickAwayListener>
    //         )}
    //       </div>
    //     </>
    //   ),
    // },
  ];

  return (
    <div className="events-table" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <MaterialTable
        className="events-table"
        style={{
          width: "100%",
          position: "relative",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          borderRadius: "12px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
        title=""
        key={muiTableKey}
        icons={tableIcons}
        tableRef={tableRef}
        columns={columns}
        options={{
          toolbar: false,
          debounceInterval: 700,
          padding: "dense",

          filtering: false,
          search: false,
          pageSize: pageSize,
          sorting: true,
          thirdSortClick: false,
          searchFieldStyle: {
            minWidth: "420px",
            paddingRight: "30px",
          },
          headerStyle: {
            borderBottomStyle: "none",
            fontWeight: 700,
            color: "#316C7A",
            fontSize: "14px",
            height: "3rem",
            fontFamily: "Work Sans",
            marginBottom: "10px",
          },
          rowStyle: (rowData) => ({
            color: "#14285A",
            backgroundColor: "#FFF",
            borderBottom: "2px solid #EEE",
            fontSize: "14px",
            fontFamily: "Work Sans",
          }),
        }}
        // actions={[
        //   {
        //     icon: () => (
        //       <FilterAltIcon
        //         style={{
        //           color: filter ? "green" : "grey",
        //         }}
        //       />
        //     ),
        //     onClick: () => handleChange(),
        //     tooltip: "Filter by Created Date",
        //     isFreeAction: true,
        //   },
        // ]}
        data={(query) => {
          if (preloadRef.current) {
            const data = {
              data: preloadRef.current.events,
              page: query.page,
              totalCount: preloadRef.current.page_metadata.total_count,
            };

            preloadRef.current = null;

            return Promise.resolve(data);
          } else {

          return new Promise((resolve) => {
            url = "/defect-inspection/v1/events?";
            url += `page=${query.page + 1}`;
            url += `&limit=${query.pageSize}`;

           

            if (searchTerm) {
              url += `&search=${encodeURIComponent(searchTerm)}`;
            }

            if (selectedDate) {
              const start = selectedDate.startDate ? dayjs(selectedDate.startDate) : (selectedDate.format ? dayjs(selectedDate) : null);
              const end = selectedDate.endDate ? dayjs(selectedDate.endDate) : (selectedDate.format ? dayjs(selectedDate) : null);

              if (start && end && start.isValid() && end.isValid()) {
                if (start.isSame(end, "day")) {
                  url += `&filter_created_date:eq=${start.format("YYYY-MM-DD")}`;
                } else {
                  url += `&filter_created_date:gte=${start.format("YYYY-MM-DD")}`;
                  url += `&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
                }
              } else if (start && start.isValid()) {
                url += `&filter_created_date:gte=${start.format("YYYY-MM-DD")}`;
              } else if (end && end.isValid()) {
                url += `&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
              }
            }

            if (deviceFilter) {
              url += `&filter_device_name:eq=${encodeURIComponent(deviceFilter)}`;
            }

            if (statusFilter) {
              url += `&filter_status:eq=${encodeURIComponent(statusFilter)}`;
            }

            if (operatorStatusFilter) {
              url += `&filter_operator_status:eq=${encodeURIComponent(operatorStatusFilter)}`;
            }

            getEvents(url).then((data) => {
              resolve({
                data: data.events,
                page: query.page,
                totalCount: filterActive? filterTableData.page_metadata.total_count : data.page_metadata.total_count,
              });
            });
          });
        }
        }}
        // components={{
        //   // Pagination: (data) => (
        //   //   <>
        //   //     {isHomePage ? (
        //   //       <div
        //   //         style={{
        //   //           display: "flex",
        //   //           justifyContent: "center",
        //   //           height: "40px",
        //   //           width: "100%",
        //   //           alignItems: "center",
        //   //         }}
        //   //       >
        //   //         <Button
        //   //           onClick={() => {
        //   //             window.location.pathname = hasAdminRole
        //   //               ? "/admin/add-events"
        //   //               : "/events/events";
        //   //           }}
        //   //           style={{ fontWeight: 600, fontSize: "16px" }}
        //   //         >
        //   //           View More
        //   //         </Button>
        //   //       </div>
        //   //     ) : (
        //   //       <TablePagination {...data} />
        //   //     )}
        //   //   </>
        //   // ),
        //   Toolbar: (props) => (
        //     <div
        //       style={{
        //         display: "flex",
        //         flexDirection: "column",
        //         alignItems: "end",
        //       }}
        //     >
        //       <MTableToolbar {...props} />
        //       {filter && (
        //         <div
        //           style={{
        //             display: "flex",
        //             alignItems: "center",
        //             marginBottom: "12px",
        //             marginRight: "24px",
        //           }}
        //         >
        //           <div
        //             style={{
        //               padding: "10px 10px",
        //             }}
        //           >
        //             <LocalizationProvider dateAdapter={AdapterDayjs}>
        //               <Stack spacing={1}>
        //                 <DesktopDatePicker
        //                   PopperProps={{
        //                     sx: {
        //                       "& .MuiPickersCalendarHeader-labelContainer ": {
        //                         maxHeight: "none !important",
        //                         fontSize: "14px",
        //                       },
        //                     },
        //                   }}
        //                   label="Filter by Created Date"
        //                   inputFormat="MM/DD/YYYY"
        //                   value={value}
        //                   onChange={handleDateChange}
        //                   renderInput={(params) => <TextField {...params} />}
        //                 />
        //               </Stack>
        //             </LocalizationProvider>
        //           </div>
        //           <div
        //             style={{
        //               cursor: "pointer",
        //               color: "red",
        //             }}
        //             onClick={handleClearFilter}
        //             tooltip="Close Filter"
        //           >
        //             <ClearIcon />
        //           </div>
        //         </div>
        //       )}
        //     </div>
        //   ),
        // }}
        // localization={{
        //   toolbar: {
        //     searchPlaceholder: "Search by Profile ID / Order Number",
        //   },
        //   body: {
        //     emptyDataSourceMessage: (
        //       <p
        //         style={{
        //           position: "absolute",
        //           top: "50%",
        //           textAlign: "center",
        //           right: "50%",
        //         }}
        //       >
        //         No records to display
        //       </p>
        //     ),
        //     height: "100%",
        //   },
        // }}
      />
    </div>
  );
};

export default ViewEventsTable;
